import React from 'react';
import { MixBoard, ChoirConductor, Modal, Input, Button } from "player-components";
import audioEngine from "../../services/audioEngine";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTour, TourProvider } from "@reactour/tour";
import formatDuration from 'date-fns/formatDuration'

import {
  selectStatus,
  selectStems,
  selectUserStems,
  unmuteChannel,
  muteChannel,
  removeChannelAsync,
  soloChannel,
  unsoloChannel,
  selectLoadedSong,
  selectSpeed,
  selectDuration,
  selectPitch,
  changeChannelGain,
  changeChannelPan,
  selectLoadingProgress,
  selectIsSafari,
  selectIsFirefox,
  addNewStem,
  disarmChannel,
  armChannel,
  attachUserMicrophoneAsync,
  mountSessionAsync,
  stopSong,
  packSessionAsync,
  mountSessionAndRenderOfflineAsync,
  selectSession,
  resetSessionMixerSettings,
  selectIntroLevel,
  selectClick1Level,
  selectClick2Level,
  selectCueLevel,
  setIntroLevel,
  setClick1Level,
  setClick2Level,
  setCueLevel,
  openSession,
  unmountSessionAsync
} from "../../features/session/sessionSlice";

import {fetchLyricsAsync, selectLyricsStatus} from '../../features/lyrics/lyricsSlice'

import LoadingScreen from "../../components/LoadingScreen";
import {
  saveExistingSession,
  selectSetlistSessions,
  resetLastCreated
} from "../../features/setlist/setlistSlice";
import NoSessionLoaded from "../../components/NoSessionLoaded";
import { ExclamationIcon, MicrophoneIcon } from "@heroicons/react/outline";
import {
  addNotificationAsync,
  selectNewTrackModalVisible,
  setNewTrackModalVisible,
  selectClientConfig,
  selectShowLyrics,
  setShowLyrics,
  setUserInputtingText,
  selectSoloMode,
  setSoloMode,
  setOfflineModalVisible,
} from "../../features/ui/uiSlice";

import {
  selectLyricsLyrics
} from "../../features/lyrics/lyricsSlice";
import { useNavigate } from "react-router-dom";

// pgw 30-sep-2025 - replace all debugging debugLog calls with debugLog
import { debugLog } from '../../debugLog';

let mountedOnce = false;

const TourGuideContainer = ({title, text}) => {

  return <div className="flex flex-col justify-center align-middle">
    <h3 className="text-lg font-bold mb-2 text-primary">{title}</h3>
    <p>{text}</p>
    {/* <Button style={{width: 100}}>Close</Button> */}
    </div>

}

const steps = [
  {
    selector: ".stem",
    content: <TourGuideContainer
      title="Song stem"
      text="This is the stem content. You can change the volume of each part by tapping the group. As you turn down the stem, it will fade out to grey."
    />,
  },
  {
    selector: "#transport",
    content: <TourGuideContainer
      title="Playback controls"
      text="Use these to control playback of the song"
    />,
  },
  {
    selector: "#section-button",
    content: <TourGuideContainer
      title="Song sections"
      text="Skip between different song sectios with this button"
    />,
  },
  {
    selector: "#solo-button",
    content: <TourGuideContainer
    title="Playback controls"
    text="Clicking this will enable solo mode, which allows you to listen to just one part at a time. Click the section you'd like to solo"
  />
  },
  {
    selector: "#lyrics-button",
    content: <TourGuideContainer
    title="Show/Hide Lyrics"
    text="View the songs lyrics while you listen to the song."
  />,
  },
  {
    selector: "#pitch-speed-button",
    content: <TourGuideContainer
    title="Speed/Pitch"
    text="You can change the speed and pitch of the playback independently"
  />,
  },
  {
    selector: "#reset-button",
    content: <TourGuideContainer
    title="Reset mix"
    text="If you'd like to reset everything back to the original settings, then tap reset!"
  />,
  },
  
  
  
];


const Session = () => {
  const dispatch = useDispatch();
  const {setSteps, setIsOpen} = useTour();
  const session = useSelector(selectSession);
  const sessions = useSelector(selectSession);
  const status = useSelector(selectStatus);
  const stems = useSelector(selectStems);
  const userStems = useSelector(selectUserStems);
  const duration = useSelector(selectDuration);
  const loadingProgress = useSelector(selectLoadingProgress);
  const soloMode = useSelector(selectSoloMode);

  const introLevel = useSelector(selectIntroLevel);
  const click1Level = useSelector(selectClick1Level);
  const click2Level = useSelector(selectClick2Level);
  const cueLevel = useSelector(selectCueLevel);

  const setlistSessions = useSelector(selectSetlistSessions);

  const newTrackModalVisible = useSelector(selectNewTrackModalVisible);

  const clientConfiguration = useSelector(selectClientConfig);

  const currentLyrics = useSelector(selectLyricsLyrics);

  const showLyrics = useSelector(selectShowLyrics);
  const lyricsStatus = useSelector(selectLyricsStatus);

  const [pendingNewChannel, setPendingNewChannel] = useState(false);
  const [lrcLyrics, setLrcLyrics] = useState(null);
  const [pendingNewChannelName, setPendingNewChannelName] = useState("");
  const [pendingDeleteChannel, setPendingDeleteChannel] = useState(false);

  const [showHeadphoneWarning, setShowHeadphoneWarning] = useState(false);
  const [showRecordArmWarning, setShowRecordArmWarning] = useState(false);

  const [currentTimeState, setCurrentTimeState] = useState(0);
  const navigate = useNavigate();

  useEffect(()=> {
    if (session.song?.lyricsUrl && !lrcLyrics && (lyricsStatus !== 'pending')) dispatch(fetchLyricsAsync(session.song.lyricsUrl));
  }, [dispatch, lrcLyrics, session])

  useEffect(() => {

		// pgw testing 19-jan-2025
        debugLog('pgw player-application/src/containers/Session/index.js pgw2_5 - p1');
		debugLog("pgw2_5 - p2, status = ", status);
		

		// debugLog(status);


      if (status === 'ready') {
        if (localStorage) {
          if (!localStorage.getItem('show_session_tour_guide')) {

			// pgw 19-jun-2025 - do not show tour guide for now...
// 			localStorage.setItem('show_session_tour_guide', 'true');
//             setIsOpen(true);

            localStorage.setItem('show_session_tour_guide', 'false');
            setIsOpen(false);


          }
        }
      }
      if (status === "unmounted" || (status !== "empty" && !mountedOnce)) {
        dispatch(stopSong());
        dispatch(mountSessionAsync({cache:clientConfiguration.features.session.cacheOnMount}));
        dispatch(setSoloMode(false));
        turnUpAllStems();
      }
      if (status === "error") {
		console.trace("error");
        dispatch(setOfflineModalVisible(true))
      }

      mountedOnce = true;
  }, [status]);

  useEffect(() => {
    
    setShowLyrics(false)
    dispatch(resetLastCreated());
    
    setLrcLyrics(null);
    // if (soloMode) {
    //   turnUpAllStems();
    //   dispatch(setSoloMode(false));
    // }
    

    setSteps(steps);
    
    return () => {
      setSteps([]);
      setIsOpen(false);
      dispatch(setShowLyrics(false));
      dispatch(stopSong());
      if (session.song?.mix) dispatch(saveExistingSession(session));
    };
  }, []);

  const updateChannelGain = (channelId, value) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      changeChannelGain({
        index: channel.index,
        value,
        userStem: channel.isInputChannel,
      })
    );
  };

  const updateChannelPan = (channelId, value) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      changeChannelPan({
        index: channel.index,
        value,
        userStem: channel.isInputChannel,
      })
    );
  };

  const updateChannelMute = (channelId) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      channel.muted
        ? unmuteChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
        : muteChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
    );
  };

  const updateChannelSolo = (channelId, solo) => {
    if (soloMode) {
      const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
      dispatch(
        changeChannelGain({
          index: channel.index,
          value: 1,
          userStem: channel.isInputChannel,
        })
      );

      const otherChannels = stems
      .concat(userStems)
      .filter((channel) => channelId !== channel.id);
      for (const otherChannel of otherChannels) {
        dispatch(
              changeChannelGain({
                index: otherChannel.index,
                value: 0,
                userStem: otherChannel.isInputChannel,
        }));
      }
    } else {
      const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
      dispatch(
        channel.soloed
          ? unsoloChannel({
              index: channel.index,
              userStem: channel.isInputChannel,
            })
          : soloChannel({
              index: channel.index,
              userStem: channel.isInputChannel,
            })
      );
    }
  };

  const updateChannelArm = async (channelId) => {
    await dispatch(attachUserMicrophoneAsync());
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      channel.armed
        ? disarmChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
        : armChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
    );
  };

  const addStem = async () => {
    dispatch(setNewTrackModalVisible(true));
    dispatch(setNewTrackModalVisible(true));
  };

  const confirmAddStem = async () => {
    if (pendingNewChannelName) {
      await dispatch(attachUserMicrophoneAsync());
      dispatch(addNewStem({ name: pendingNewChannelName }));
      setPendingNewChannelName("");
      dispatch(setNewTrackModalVisible(false));

	  // pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
      /*
		dispatch(
        addNotificationAsync({
          title: "Track added to session",
          text: `${pendingNewChannelName} added to the session
        `,
          type: "positive",
        })
      );
		*/

    }
  };

  const shareSession = async () => {
    await dispatch(packSessionAsync());

    // pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
	/*
    dispatch(
      addNotificationAsync({
        title: "Session downloaded",
        text: `The session was packed up into a .session file and downloaded`,
        type: "positive",
      })
    );
	*/

  };

  const downloadSession = async () => {
    await dispatch(saveExistingSession(session));
    await dispatch(mountSessionAndRenderOfflineAsync({cache:clientConfiguration.features.session.cacheOnMount}));
	
	// pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
	/*
    dispatch(
      addNotificationAsync({
        title: "Session downloaded",
        text: `The session mix was bounced down and downloaded.`,
        type: "positive",
      })
    );
	*/

  };

  const resetSession = async () => {
    await dispatch(resetSessionMixerSettings());


    dispatch(
      addNotificationAsync({
        title: "Session reset",
        text: `All the mixer settings for the session were reset.`,
        type: "positive",
      })
    );

  };

  const deleteStem = async (channelId) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    if (channel.isInputChannel) {
      setPendingDeleteChannel(channel);
    }
  };

  const confirmDeleteTrack = async () => {
    await dispatch(removeChannelAsync(pendingDeleteChannel));
    setPendingDeleteChannel(false);
  };

  const handleCancel = async () => {
    await dispatch(unmountSessionAsync());
	// pgw 29-sep-2025
	debugLog('player-application/src/containers/Session/index.js handleCancel navigating to /library');
    navigate('/library');
    setTimeout(()=>window.location.reload(),100);
  }

  const formLrcLyrics = (lyricsArray) => {
    let lrcString = '';
    for (const line of lyricsArray) {
      lrcString = lrcString + `[${new Date(1000 * line.time).toISOString().substr(14, 8)}] ${line.text}\n`
    }
    return lrcString;
  }

  useEffect(()=>{
    if (currentLyrics) {
      setLrcLyrics(formLrcLyrics(currentLyrics));
    } else {
      setLrcLyrics(null)
    }
  },[currentLyrics])

  useEffect(()=>{
    const currentTimeInterval = setInterval(()=>{
      setCurrentTimeState(audioEngine.currentPosition);
    },100);
    return ()=>{
      
      
      dispatch(setSoloMode(false));
      // turnUpAllStems()
      clearInterval(currentTimeInterval)
    }
  },[])

  const turnDownAllStems = () => {
    for (const stem of stems.concat(userStems)) {
      dispatch(
        changeChannelGain({
          index: stem.index,
          value: 0,
          userStem: stem.isInputChannel,
        })
      );
    }
  }

  const turnUpAllStems = () => {
    for (const stem of stems.concat(userStems)) {
      dispatch(
        changeChannelGain({
          index: stem.index,
          value: 1,
          userStem: stem.isInputChannel,
        })
      );
    }
  }

  useEffect(()=>{
    if (soloMode) {
     
      //turn down level of all channels
      // turnDownAllStems()
      turnUpAllStems()
    } else {
      // setIsOpen(false);
      //unmute all channels
      // turnUpAllStems()
    }
  },[soloMode])

  const renderMixer = () => {
    switch (clientConfiguration.features.session.mixerTemplate) {
      case 'choir':
        return <ChoirConductor
          showMixRecord={clientConfiguration.features.session.showMixRecord}
          showMixControls={clientConfiguration.features.session.showMixControls}
          stems={stems.concat(userStems)}
          getVuLevel={audioEngine.getChannelPeak}
          onFaderChange={updateChannelGain}
          onPanChange={updateChannelPan}
          onMuteStem={updateChannelMute}
          onSoloStem={updateChannelSolo}
          onArmStem={updateChannelArm}
          onDeleteStem={deleteStem}
          onSave={(a, b, c) => {
            // debugLog(a, b, c);
          }}
          onAddStem={addStem}
          onDownload={downloadSession}
          onShare={shareSession}
          onReset={resetSession}
          onChangeIntroLevel={(newValue) => dispatch(setIntroLevel(newValue))}
          onChangeClick1Level={(newValue) => dispatch(setClick1Level(newValue))}
          onChangeClick2Level={(newValue) => dispatch(setClick2Level(newValue))}
          onChangeCueLevel={(newValue) => dispatch(setCueLevel(newValue))}
          // onSoloModeToggle={handleSoloModeToggle}
          step={0.5}
          showGuideChannelButtons={false}
          introLevel={introLevel}
          click1Level={click1Level}
          click2Level={click2Level}
          cueLevel={cueLevel}
          lyrics={lrcLyrics}
          currentTimeMilliseconds={currentTimeState}
          showLyricsButton={true}
          lyricsActive={showLyrics}
          onClickLyricsButton={()=>dispatch(setShowLyrics(!showLyrics))}
          soloMode={soloMode}
      />
        
      case 'band':
        return <MixBoard
          stems={stems.concat(userStems)}
          getVuLevel={audioEngine.getChannelPeak}
          onFaderChange={updateChannelGain}
          onPanChange={updateChannelPan}
          onMuteStem={updateChannelMute}
          onSoloStem={updateChannelSolo}
          onArmStem={updateChannelArm}
          onDeleteStem={deleteStem}
          onSave={(a, b, c) => {
            // debugLog(a, b, c);
          }}
          onAddStem={addStem}
          onDownload={downloadSession}
          onShare={shareSession}
          onReset={resetSession}
          onChangeIntroLevel={(newValue) => dispatch(setIntroLevel(newValue))}
          onChangeClick1Level={(newValue) => dispatch(setClick1Level(newValue))}
          onChangeClick2Level={(newValue) => dispatch(setClick2Level(newValue))}
          onChangeCueLevel={(newValue) => dispatch(setCueLevel(newValue))}
          step={0.5}
          showGuideChannelButtons={false}
          introLevel={introLevel}
          click1Level={click1Level}
          click2Level={click2Level}
          cueLevel={cueLevel}
        />
      default:
        return <div>No template provided</div>
    }
  }

  const getLoadingText = () => {
    switch (loadingProgress.step) {
      case 0:
        return "Booting session...";
      case 1:
        return "Downloading song";
      case 2:
        return "Downloading song";
      case 3:
        return "Loading song into memory";
      case 4:
        return "Starting engine...";
      case 5:
        return "Ready to roll...";
      case 6:
        return "Packing up session...";
      case 7:
        return "Loading offline render...";
      case 8:
        return "Rendering bounce...";
      case 9:
        return "Creating download...";
      default:
        break;
    }
  };
  // debugLog(stems);
  return status === "ready" && mountedOnce ? (
    <>
      {renderMixer()}
      <Modal
        isOpen={pendingDeleteChannel ? true : false}
        title="Delete channel?"
        text={
          <>
            <span>
              Are you sure you'd like to remove{" "}
              <b>{pendingDeleteChannel.label}</b> from the session and all
              associated recordings that have been made on it?
            </span>
          </>
        }
        confirmLabel="Yes, delete it"
        icon={
          <ExclamationIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        closeLabel="Cancel"
        onClose={() => {
          setPendingDeleteChannel(false);
        }}
        onConfirm={confirmDeleteTrack}
      />
      <Modal
        isOpen={newTrackModalVisible}
        title="New channel"
        content={
          <div className="w-full">
            <p>Please provide a name for the new channel</p>
            <Input
              onChange={(e) => setPendingNewChannelName(e.target.value)}
              placeholder="eg Vocals"
              autofocus
              label="Name"
              size="md"
              type="text"
              className="w-full border rounded-md my-4 p-2"
              autocomplete="off"
            ></Input>
          </div>
        }
        confirmLabel="Add"
        icon={
          <MicrophoneIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        closeLabel="Cancel"
        onClose={() => {
          
          dispatch(setUserInputtingText(false));
        }}
        onConfirm={confirmAddStem}
      />
      <Modal
        isOpen={showHeadphoneWarning ? true : false}
        title="Have you got headphones plugged in?"
        text="You'll want to use headphones while recording to prevent feedback from the speakers"
        confirmLabel="Okay"
        icon={
          <MicrophoneIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        onConfirm={() => setShowHeadphoneWarning(false)}
      />
      <Modal
        isOpen={showRecordArmWarning ? true : false}
        title="No armed tracks"
        text="You'll need to add a track and record arm it to record into this session"
        confirmLabel="Okay"
        icon={
          <MicrophoneIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        onConfirm={() => setShowRecordArmWarning(false)}
      />
    </>
  ) : status === "empty" ? (
    <NoSessionLoaded setlistEnabled={clientConfiguration.features.setlist.enabled} setlistSessions={setlistSessions} />
  ) : (
    <LoadingScreen
      animationFile={clientConfiguration.features.session.loadingAnimationFile}
      title="Loading session"
      text={getLoadingText()}
      loadingProgress={loadingProgress}
      totalSteps={9}
      onCancel={handleCancel}
    />
  );
};

export default Session;
